import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import adminApi from "../../../api/adminApi";
import { toast } from "react-toastify";

const PROVIDER_MODELS = {
  deepseek: ["deepseek-chat", "deepseek-reasoner"],
  openrouter: [
    "openrouter/free",
    "nvidia/nemotron-3-ultra-550b-a55b:free",
    "nvidia/nemotron-3-super-120b-a12b:free",
    "nvidia/nemotron-3.5-lightning:free",
  ],
  gemini: ["gemini-2.0-flash", "gemini-1.5-flash", "gemini-1.5-pro"],
};

const useSettings = () =>
  useQuery("admin-settings", () => adminApi.getSettings(), { staleTime: 60000 });

const useProviderStatus = () =>
  useQuery("ai-providers", () => adminApi.getAiProviders(), { staleTime: 60000 });

const useUsageStats = () =>
  useQuery("ai-usage-stats", () => adminApi.getAiUsageStats(), { staleTime: 30000 });

const AdminSettings = () => {
  const { data, isLoading } = useSettings();
  const { data: providerData } = useProviderStatus();
  const { data: usageData } = useUsageStats();
  const queryClient = useQueryClient();
  const [localSettings, setLocalSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (data?.data) setLocalSettings(data.data);
  }, [data]);

  const handleUpdateSetting = async (key, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));

    setSaving(true);
    try {
      await adminApi.updateSetting(key, value);
      queryClient.invalidateQueries("admin-settings");
      toast.success("Setting updated");
    } catch (err) {
      setLocalSettings((prev) => ({
        ...prev,
        [key]: { ...prev[key], value: prev[key]?.value },
      }));
      toast.error("Failed to update setting");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminApi.testAiConnection({
        provider: localSettings.ai_provider?.value,
        model: localSettings.ai_model?.value,
      });
      setTestResult(res.data);
      if (res.data.success) {
        toast.success("Connection successful");
      } else {
        toast.error("Connection failed");
      }
    } catch (err) {
      setTestResult({ success: false, error: err.message });
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleProviderChange = (newProvider) => {
    handleUpdateSetting("ai_provider", newProvider);
    const firstModel = PROVIDER_MODELS[newProvider]?.[0] || "";
    handleUpdateSetting("ai_model", firstModel);
    setTestResult(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-8 w-48 rounded"></div>
        <div className="skeleton h-32 rounded-lg"></div>
        <div className="skeleton h-32 rounded-lg"></div>
      </div>
    );
  }

  const currentProvider = localSettings.ai_provider?.value || "deepseek";
  const currentModel = localSettings.ai_model?.value || "";
  const aiEnabled = localSettings.ai_enabled?.value !== false;
  const providers = providerData?.data || [];
  const usage = usageData?.data;

  return (
    <div className="space-y-6 max-w-3xl">
      <h1 className="text-2xl font-bold">System Settings</h1>

      {/* Card 1: AI Service Configuration */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">
            <i className="fa-solid fa-robot text-blue-600"></i> AI Service Configuration
          </h2>
          <p className="text-sm text-gray-500">
            Configure the AI provider used for generating insights and prediction explanations.
          </p>

          <div className="divider"></div>

          {/* AI Enabled Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-sm">AI Enabled</div>
              <div className="text-xs text-gray-400">
                {aiEnabled ? "AI service is active" : "AI service is disabled"}
              </div>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={aiEnabled}
              onChange={() => handleUpdateSetting("ai_enabled", !aiEnabled)}
              disabled={saving}
            />
          </div>

          <div className="divider"></div>

          {/* Provider Selection */}
          <div className="space-y-3">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium text-sm">Active Provider</span>
              </label>
              <select
                className="select select-bordered select-sm w-full"
                value={currentProvider}
                onChange={(e) => handleProviderChange(e.target.value)}
                disabled={saving || !aiEnabled}
              >
                {providers.map((p) => (
                  <option key={p.name} value={p.name} disabled={!p.configured}>
                    {p.label} {!p.configured ? "(Not Configured)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Model Selection */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium text-sm">Active Model</span>
              </label>
              <select
                className="select select-bordered select-sm w-full"
                value={currentModel}
                onChange={(e) => handleUpdateSetting("ai_model", e.target.value)}
                disabled={saving || !aiEnabled}
              >
                {(PROVIDER_MODELS[currentProvider] || []).map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>

            {/* Provider Status */}
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <div
                  key={p.name}
                  className={`badge badge-sm gap-1 ${
                    p.configured
                      ? p.name === currentProvider
                        ? "badge-primary"
                        : "badge-success"
                      : "badge-ghost"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      p.configured ? "bg-green-400" : "bg-gray-400"
                    }`}
                  ></span>
                  {p.label}
                </div>
              ))}
            </div>
          </div>

          <div className="divider"></div>

          {/* Test Connection */}
          <div className="flex items-center gap-3">
            <button
              className="btn btn-sm btn-outline btn-primary"
              onClick={handleTestConnection}
              disabled={testing || !aiEnabled || !aiEnabled}
            >
              {testing ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Testing...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-plug"></i> Test Connection
                </>
              )}
            </button>

            {testResult && (
              <div
                className={`text-sm flex items-center gap-2 ${
                  testResult.success ? "text-green-600" : "text-red-600"
                }`}
              >
                {testResult.success ? (
                  <>
                    <i className="fa-solid fa-circle-check"></i>
                    <span>
                      Connected ({testResult.responseTime}ms) using {testResult.model}
                    </span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-circle-xmark"></i>
                    <span>{testResult.error}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card 2: AI Feature Toggles */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">
            <i className="fa-solid fa-sliders text-purple-600"></i> AI Feature Toggles
          </h2>
          <p className="text-sm text-gray-500">
            Control which AI features are enabled.
          </p>

          <div className="divider"></div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">AI Insights</div>
                <div className="text-xs text-gray-400">
                  Generate insights and recommendations from analytics data
                </div>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-primary toggle-sm"
                checked={localSettings.ai_feature_insights?.value !== false}
                onChange={() =>
                  handleUpdateSetting(
                    "ai_feature_insights",
                    localSettings.ai_feature_insights?.value !== false ? false : true
                  )
                }
                disabled={saving || !aiEnabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">AI Explanations</div>
                <div className="text-xs text-gray-400">
                  Generate plain-language explanations for blood demand predictions
                </div>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-primary toggle-sm"
                checked={localSettings.ai_feature_explanations?.value !== false}
                onChange={() =>
                  handleUpdateSetting(
                    "ai_feature_explanations",
                    localSettings.ai_feature_explanations?.value !== false ? false : true
                  )
                }
                disabled={saving || !aiEnabled}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Card 3: Limits & Fallback */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">
            <i className="fa-solid fa-shield-halved text-orange-600"></i> Limits & Fallback
          </h2>
          <p className="text-sm text-gray-500">
            Configure daily limits and fallback behavior.
          </p>

          <div className="divider"></div>

          {/* Daily Limit */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium text-sm">Daily AI Request Limit</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="input input-bordered input-sm w-32"
                value={localSettings.ai_daily_limit?.value || 0}
                min={0}
                onChange={(e) =>
                  handleUpdateSetting("ai_daily_limit", parseInt(e.target.value) || 0)
                }
                disabled={saving || !aiEnabled}
              />
              <span className="text-xs text-gray-400">
                0 = unlimited
              </span>
            </div>
          </div>

          <div className="divider"></div>

          {/* Fallback */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">Enable AI Fallback</div>
                <div className="text-xs text-gray-400">
                  If the primary provider fails, try a secondary provider
                </div>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-primary toggle-sm"
                checked={localSettings.ai_fallback_enabled?.value === true}
                onChange={() =>
                  handleUpdateSetting(
                    "ai_fallback_enabled",
                    localSettings.ai_fallback_enabled?.value === true ? false : true
                  )
                }
                disabled={saving || !aiEnabled}
              />
            </div>

            {localSettings.ai_fallback_enabled?.value === true && (
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium text-sm">Fallback Provider</span>
                </label>
                <select
                  className="select select-bordered select-sm w-full"
                  value={localSettings.ai_fallback_provider?.value || ""}
                  onChange={(e) => handleUpdateSetting("ai_fallback_provider", e.target.value || null)}
                  disabled={saving || !aiEnabled}
                >
                  <option value="">None</option>
                  {providers
                    .filter((p) => p.configured && p.name !== currentProvider)
                    .map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.label}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card 4: AI Usage Statistics */}
      {usage && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">
              <i className="fa-solid fa-chart-simple text-teal-600"></i> AI Usage Statistics
            </h2>
            <p className="text-sm text-gray-500">
              Overview of AI API usage.
            </p>

            <div className="divider"></div>

            <div className="grid grid-cols-2 gap-4">
              <div className="stat bg-base-200 rounded-lg p-3">
                <div className="stat-title text-xs">Today</div>
                <div className="stat-value text-lg">{usage.today.requests}</div>
                <div className="stat-desc text-xs">
                  {usage.today.successful} success / {usage.today.failed} failed
                  {usage.today.totalTokens > 0 && ` / ${usage.today.totalTokens} tokens`}
                </div>
              </div>
              <div className="stat bg-base-200 rounded-lg p-3">
                <div className="stat-title text-xs">Last 7 Days</div>
                <div className="stat-value text-lg">{usage.week.requests}</div>
                <div className="stat-desc text-xs">
                  {usage.week.successful} success / {usage.week.failed} failed
                  {usage.week.totalTokens > 0 && ` / ${usage.week.totalTokens} tokens`}
                </div>
              </div>
            </div>

            {usage.byProvider.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-medium text-gray-500 mb-1">By Provider</div>
                <div className="space-y-1">
                  {usage.byProvider.map((p) => (
                    <div
                      key={p.provider}
                      className="flex items-center justify-between text-xs bg-base-200 rounded px-3 py-1.5"
                    >
                      <span className="font-medium">{p.providerLabel}</span>
                      <span className="text-gray-500">
                        {p.requests} req / {p.successful} ok / {p.failed} fail
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Card 5: Local Prediction Engine (existing, unchanged) */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">
            <i className="fa-solid fa-gauge text-green-600"></i> Local Prediction Engine
          </h2>
          <p className="text-sm text-gray-500">
            The built-in statistical prediction engine is always active.
          </p>

          <div className="divider"></div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <i className="fa-solid fa-check text-green-500"></i>
              <span>Exponential Smoothing</span>
              <span className="badge badge-sm badge-success">Active</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <i className="fa-solid fa-check text-green-500"></i>
              <span>Linear Trend Extrapolation</span>
              <span className="badge badge-sm badge-success">Active</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <i className="fa-solid fa-check text-green-500"></i>
              <span>Weekly Seasonality Detection</span>
              <span className="badge badge-sm badge-success">Active</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <i className="fa-solid fa-check text-green-500"></i>
              <span>Demand Classification (LOW/MEDIUM/HIGH/CRITICAL)</span>
              <span className="badge badge-sm badge-success">Active</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <i className="fa-solid fa-check text-green-500"></i>
              <span>Confidence Scoring</span>
              <span className="badge badge-sm badge-success">Active</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <i className="fa-solid fa-check text-green-500"></i>
              <span>Background Prediction Refresh (6h)</span>
              <span className="badge badge-sm badge-success">Active</span>
            </div>
          </div>

          <div className="divider"></div>

          <div className="alert alert-info text-sm">
            <i className="fa-solid fa-circle-info"></i>
            <span>
              The local prediction engine always runs regardless of AI settings. Disabling AI
              only removes AI-generated explanations and recommendations.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
